import React from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useAppContext } from "../../context/AppContext";
import { useCopy } from "../../context/CopyContext";
import Header from "../Header";
import Button from "../Button";
import MultiLineCopy from "../MultiLineCopy";
import PoweredBy from "../PoweredBy";
import Heading from "../Heading";

const ConfirmationView: React.FC = () => {
  const navigate = useNavigate();
  const { state, dispatch } = useAppContext();
  const { getCopy } = useCopy();

  // Get selected reward method or fallback for guests
  const getRewardValue = () => {
    if (state.selectedRewardMethod) {
      switch (state.selectedRewardMethod) {
        case 'loyalty_points':
          return '500 pts';
        case 'digital_cash':
          return '$5.00';
        case 'discount_coupon':
          return '5% OFF';
        default:
          return '500 pts';
      }
    } else if (state.isGuest) {
      // For guests, calculate discount percentage
      if (state.empties.length > 0) {
        const totalDiscount = state.empties.reduce((avg, empty) => {
          if (empty.product.isBranded && empty.product.rewards) {
            return avg + (parseInt(empty.product.rewards.discountValue) * empty.quantity);
          }
          return avg;
        }, 0);
        const totalItems = state.empties.reduce((total, empty) => total + empty.quantity, 0);
        const percentage = totalItems > 0 ? Math.round(totalDiscount / totalItems) : 5;
        return `${percentage}% OFF`;
      }
      return '5% OFF';
    }
    return '500 pts'; // Default fallback
  };

  const handleFinished = () => {
    console.log("User finished the process");
    // Reset all state (clear items, user details, etc.)
    dispatch({ type: 'RESET' });
    // Navigate back to start
    navigate("/");
  };

  return (
    <div
      className="bg-brand-background min-h-screen flex flex-col relative overflow-hidden"
      data-node-id="166:5181"
    >
      {/* Content Container */}
      <div className="relative z-10 flex-1 flex flex-col gap-24">
        {/* Header Section - with padding */}
        <div className="flex justify-center mx-20 mt-20">
          <div className="w-full max-w-[920px]">
            <Header
              showItems={false}
              itemsNumber={state.itemsCount.toString()}
              emailAddress={state.userEmail}
              showEmailAddress={false}
              isMember={state.isMember}
              isGuest={state.isGuest}
              animateIcon={false}
            />
          </div>
        </div>

        {/* Main Content Area - with 3-row grid */}
        <motion.div 
          className="flex-1 grid grid-rows-3 gap-0 mx-20"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
        >
          {/* Row 1 - Main heading */}
          <div className="flex items-center justify-center row-span-2">
            <div className="text-center w-full max-w-[920px]">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ 
                  duration: 0.6, 
                  ease: [0.25, 0.1, 0.25, 1]
                }}
              >
                <Heading
                  title={getCopy('confirmation.heading')}
                  subtitle={getCopy('confirmation.subheading')}
                  titleWeight="bold"
                  className="mb-8"
                />
              </motion.div>
            </div>
          </div>

          {/* Row 3 - Help text */}
          <div className="flex items-end justify-center">
            <motion.div
              className="text-center w-full max-w-[680px]"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ 
                duration: 0.6, 
                delay: 0.4,
                ease: [0.25, 0.1, 0.25, 1]
              }}
            >
              <MultiLineCopy 
                text={getCopy('confirmation.helpText')}
                className="font-brand-secondary font-light text-[32px] leading-[42px] text-brand-text-primary"
              />
            </motion.div>
          </div>
        </motion.div>

        {/* Bottom Section - Finished button and footer */}
        <div className="flex flex-col gap-16 items-center mx-20 mb-10">
          {/* Finished Button */}
          <div className="flex justify-center w-full max-w-[500px]">
            <motion.div
              className="w-full"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ 
                duration: 0.6, 
                delay: 0.6,
                ease: [0.25, 0.1, 0.25, 1]
              }}
            >
              <Button variant="primary" onClick={handleFinished} className="w-full">
                {getCopy('confirmation.buttons.primary')}
              </Button>
            </motion.div>
          </div>

          {/* Footer */}
          <div className="flex justify-center">
            <PoweredBy />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationView;
